import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Product {
  id: string;
  name: string;
  shortName: string;
  description: string;
  imageFilename: string | null;
  category: string;
  hasFallDetection: boolean;
  basePrice: number;
  setupFee: number;
  features: string[];
  isActive: boolean;
  sortOrder: number;
  pricingPlans: PricingPlan[];
}

interface PricingPlan {
  id: string;
  productId: string;
  planType: string;
  name: string;
  billingCycle: string;
  monthlyPrice: number;
  annualTotal: number | null;
  setupFee: number;
  activationFee: number;
  savings: number | null;
  isActive: boolean;
  sortOrder: number;
}

interface ProductsContextType {
  products: Product[];
  loading: boolean;
  error: string | null;
  getProductByShortName: (shortName: string) => Product | undefined;
  getProductPlansByShortName: (shortName: string) => PricingPlan[];
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

export const ProductsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/products`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setProducts(data.products || []);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch products');
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [API_URL]);

  const getProductByShortName = (shortName: string): Product | undefined => {
    return products.find(p => p.shortName === shortName);
  };

  const getProductPlansByShortName = (shortName: string): PricingPlan[] => {
    const product = getProductByShortName(shortName);
    return product?.pricingPlans || [];
  };

  return (
    <ProductsContext.Provider value={{ 
      products, 
      loading, 
      error, 
      getProductByShortName,
      getProductPlansByShortName
    }}>
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

